<template>
  <table class="tabloid">
    <thead>
      <th v-for="(column, columnName) in columns" :key="`column-${name}-${columnName}`" @click="sort(columnName)" v-bind="column.headerAttributes">
        <template v-if="column.title">{{ column.title }}</template>
        <template v-else>{{ columnName | cz }}</template>&nbsp;
        <i v-if="sortedByFull == columnName && sortedAscFull == 1" class="fa fa-caret-up"></i>
        <i v-if="sortedByFull == columnName && sortedAscFull == -1" class="fa fa-caret-down"></i>
      </th>
    </thead>
    <tbody>
      <template v-for="(row, index) in sortedDataList">
        <tr
          :key="`row-${name}-${index}`"
          :class="{ striped: index % 2 == 1, ...(row.rowAttributes != null ? row.rowAttributes : {}).class }"
          v-bind="row.rowAttributes"
          @click="rowClick(row)"
        >
          <td
            v-for="(column, columnName) in columns"
            :data-columnName="columnName"
            :data-title="column.title != null ? column.title : $options.filters.cz(columnName)"
            v-bind="column.getCellAttributes && column.getCellAttributes(row)"
            :key="`cell-${name}-${index}-${columnName}`"
          >
            <slot :name="columnName" :value="row[columnName]" :index="index" :row="row" :column="column">
              {{ row[columnName] }}
            </slot>
          </td>
        </tr>
        <tr
          :key="`row-${name}-${index}-2`"
          v-if="row.secondRow"
          :class="{ striped: index % 2 == 1, ...(row.rowAttributes != null ? row.rowAttributes : {}).class }"
          v-bind="row.secondRowAttributes"
        >
          <slot name="secondRow" :row="row" :index="index"></slot>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<script>
export default {
  name: "tabloid",
  props: {
    name: {},
    defaultOrderBy: {},
    defaultOrderAsc: {},
    columns: {},
    data: {}
  },
  data: () => ({
    sortedBy: null,
    sortedAsc: null,
    originDataMap: null, // don't change order during item editation
  }),
  computed: {
    sortedByFull() {
      if (this.sortedBy != null) {
        return this.sortedBy;
      }

      if (this.defaultOrderBy != null) {
        return this.defaultOrderBy;
      }

      return 'id';
    },
    sortedAscFull() {
      if (this.sortedAsc != null) {
        return this.sortedAsc;
      }

      if (this.defaultOrderAsc != null) {
        return this.defaultOrderAsc;
      }

      return 1;
    },
    dataAsList() {
      if (Array.isArray(this.data)) {
        return this.data
          .map((row, id) => ({ ...row, id })) // add index as id
      }

      return Object.keys(this.data)
        .map(id => ({ ...this.data[id], id })); // convert to array, add id to object
    },
    dataAsMap() {
      if (Array.isArray(this.data)) {
        return this.data
          .map((row, id) => ({ row, id })) // index as id
          .reduce((res, pair) => { res[pair.id] = pair.row; return res; }, {}) // to object
      }

      return this.data;
    },
    sortedDataList() {
      // new item on top even without sorting
      if (this.originDataMap == null || Object.keys(this.originDataMap) == 0) {
        this.originDataMap = this.dataAsMap;
      }

      // don't change order during item editation, but accept added items
      const originData = this.originDataMap != null && Object.keys(this.originDataMap).length == Object.keys(this.dataAsMap).length
        ? this.originDataMap
        : this.dataAsMap;

      const getValueOrDefault = val => {
        if (val == null) {
          return " ";
        }

        if (typeof val == 'string') {
          return val.toLowerCase();
        }

        return val;
      };
      const isNew = item => {
        // not loaded
        if (this.originDataMap == null || this.dataAsMap == null) {
          return false;
        }

        // is in origin
        if (this.originDataMap[item.id] != null) {
          return false;
        }

        // is not in incoming
        if (this.dataAsMap[item.id] == null) {
          return false;
        }

        // is not in origin and is in incoming
        return true;
      };

      let list = [ ...this.dataAsList ]; // clone
      list.sort((i1, i2) => {
        if (isNew(i1)) {
          return -1;
        }
        if (isNew(i2)) {
          return 1;
        }

        var val1 = this.columns[this.sortedByFull] != null && this.columns[this.sortedByFull].orderBy != null
          ? this.columns[this.sortedByFull].orderBy(originData[i1.id][this.sortedByFull], originData[i1.id])
          : originData[i1.id][this.sortedByFull];
        var val2 = this.columns[this.sortedByFull] != null && this.columns[this.sortedByFull].orderBy != null
          ? this.columns[this.sortedByFull].orderBy(originData[i2.id][this.sortedByFull], originData[i2.id])
          : originData[i2.id][this.sortedByFull];

        return getValueOrDefault(val1) < getValueOrDefault(val2)
          ? -this.sortedAscFull
          : this.sortedAscFull;
      });

      return list;
    }
  },
  methods: {
    sort(sortBy) {
      // save data in time of sort
      this.originDataMap = this.dataAsMap;

      if (sortBy == this.sortedByFull) {
        this.sortedAsc = -this.sortedAsc;
      } else {
        this.sortedBy = sortBy;
        this.sortedAsc = 1;
      }
    },
    rowClick(row) {
      if (typeof row.onRowClick != 'undefined') {
        row.onRowClick();
      }
    },
  },
};
</script>

<style lang="sass">
@import ../sass/_constants

.tabloid
  margin: 1rem 0 1rem 0
  width: 100%
  border-spacing: 0
  th, td
    padding: .5rem 1rem
    text-align: center
  thead th
    padding: 10px 20px
    color: $c-primary-inverse
    background-color: $c-primary
    cursor: pointer
    font-weight: bold
    &[title]
      cursor: help
  tbody tr
    background-color: $bc-secondary-light
    &.striped
      background-color: $bc-secondary-lighter

@media only screen and (max-width: 1000px)
  .tabloid
    display: grid
    
    thead
      display: none
    tbody
      display: grid

    tr
      display: grid

      td
        display: grid
        grid-auto-flow: column
        grid-template-columns: 1fr 2fr
        grid-gap: 1rem
        justify-items: start

        &:before
          content: attr(data-title)
          justify-self: end
</style>
